import {customElement, property} from 'lit/decorators.js';
import {CSSResult, html, LitElement, nothing, unsafeCSS} from 'lit';
import {consume} from '@lit/context';
import styles from './remove-collection-layer.scss';
import {collectionMenuContext} from '../../../scripts/context/menuContext.js';
import {CollectionMenuState} from '../../../scripts/context/menu-state.js';
import {i18nContext} from '../../../scripts/context/i18nContext.js';
import {CollectionTrackingContext, Messages, WishlistCollection} from '../../../@types/wishlist.js';
import {collectionApi} from '../../../scripts/service/collection-api.js';
import {
  WishlistAppConfig,
  wishlistAppConfigContext,
  wishlistRouterContext
} from '../../../scripts/context/appConfigContext.js';
import {WishlistRouter} from '../wishlist-app/wishlist-router.js';
import {pushToSnackbar} from '../../../scripts/service/snackbar-service.js';
import {collectionsContext, productsContext} from '../../../scripts/context/wishlistContext.js';
import {CollectionsState, ProductsState} from '../../../scripts/context/wishlist-state.js';
import {sharedCss} from '../../../scripts/style-bundle/shared.js';
import {REMOVE_COUNT_FROM_WISHLIST_EVENT} from '../../../scripts/service/config.js';
import {getBackButton} from '../base-layer/base-layer.js';
import baseLayerStyles from '../base-layer/base-layer.scss';

@customElement('ents-remove-collection-layer')
export class RemoveCollectionLayer extends LitElement {
  @consume({ context: i18nContext })
  private messages!: Messages;

  @consume({ context: collectionMenuContext, subscribe: true })
  @property({ attribute: false })
  private collectionMenuState!: CollectionMenuState;

  @consume({ context: wishlistAppConfigContext })
  private wishlistAppConfig!: WishlistAppConfig;

  @consume({ context: wishlistRouterContext })
  private wishlistRouter!: WishlistRouter;

  @consume({ context: collectionsContext, subscribe: true })
  @property({ attribute: false })
  private collectionsState!: CollectionsState;

  @consume({context: productsContext, subscribe: true})
  @property({attribute: false})
  private productsState!: ProductsState;

  static get styles(): CSSResult[] {
    return [sharedCss, unsafeCSS(styles), unsafeCSS(baseLayerStyles)];
  }

  protected render(): unknown {
    const collection = this.collectionMenuState.context.collection!;
    const collectionTrackingContext = this.collectionMenuState.context.collectionTrackingContext!;

    if (this.canPerformQuickDeletion(collection)){
      this.removeCollection(
        collection,
        collectionTrackingContext,
        false,
        true
      );
      return nothing;
    }

    const safeCollectionName = this.getSafeCollectionNameOrEmpty();
    return html`
        <ents-base-layer
                drawer-test-id="ents-remove-collection-layer"
                ?showLayer="${this.collectionMenuState.context.openRemoveCollectionView}"
                @ents-layer-closed="${this.closeLayer}"
        >
            ${this.collectionMenuState.context.backButtonCallback != undefined ?
                    getBackButton(this.messages, 'ents-remove-collection-layer', this.backButtonAction.bind(this)) : nothing}
            <div class="ents-remove-collection-layer"
                data-test-ents-remove-collection-layer>
                <h2 class="ents-heading-medium">${this.messages['ents_wishlist_collection_remove_title']}</h2>
                <p class="fs-mask">${this.messages['ents_wishlist_collection_remove_description']?.replace('{}', safeCollectionName)}</p>
                <button class="bdk-button" type="button" variant="outline" size="medium"
                        data-test-ents-remove-collection-only-button
                        @click="${() => this.removeCollection(collection, collectionTrackingContext, false)}">
                    ${this.messages['ents_wishlist_collection_remove_listOnly']}
                </button>
                <button class="bdk-button" type="button" variant="outline" size="medium"
                        data-test-ents-remove-collection-and-favorites-button
                        @click="${() => this.removeCollection(collection, collectionTrackingContext, true)}">
                    ${this.messages['ents_wishlist_collection_remove_listAndFavorites']}
                </button>
            </div>
        </ents-base-layer>
    `;
  }

  private canPerformQuickDeletion(collection: WishlistCollection){
    if (!this.collectionMenuState.context.openRemoveCollectionView){
      return false;
    }
    const collectionSize =
        this.productsState.entities.filter(
          e => e.collectionReferences.map(c => c.collectionId).includes(collection.id)
        ).length;

    return collectionSize == 0;
  }

  private async removeCollection(
    collection: WishlistCollection,
    collectionTrackingContext: CollectionTrackingContext,
    removeItemsFromFavorites: boolean,
    deletedViaQuickDelete = false
  ) {
    const collectionEndpoint = this.wishlistAppConfig.collectionEndpoint;
    const collectionId = collection.id;
    collectionApi.delete(collectionEndpoint, collectionId, removeItemsFromFavorites).then((result) => {
      if (result?.isOk) {
        if (removeItemsFromFavorites) {
          const productsOfCollectionToDelete = this.productsState.getProductsToRender(collectionId);
          this.productsState.updateAll(
            this.productsState.entities.filter(product =>
              !productsOfCollectionToDelete.some(pDelete => pDelete.syan === product.syan && (pDelete.colorId ? pDelete.colorId === product.colorId : true))));
          this.dispatchRemovedCountEvent(productsOfCollectionToDelete.length);
        }
        this.collectionsState.delete(collection, collectionTrackingContext, removeItemsFromFavorites);
      }
    });
    if (window.location.pathname === collectionEndpoint) {
      this.showSnackbar(collection, removeItemsFromFavorites, deletedViaQuickDelete);
    } else {
      await this.wishlistRouter.gotoWithHistory(collectionEndpoint, collectionEndpoint, true);
      setTimeout(() => {
        this.showSnackbar(collection, removeItemsFromFavorites, deletedViaQuickDelete);
      }, 50);
    }
  }

  private showSnackbar(
    collection: WishlistCollection,
    removeItemsFromFavorites: boolean,
    wasDeletedViaQuickDelete = false,
  ) {
    let collectionDeletedText;
    if (removeItemsFromFavorites) {
      collectionDeletedText = this.messages['ents_wishlist_collection_removedListAndArticles'];
    } else {
      collectionDeletedText = this.messages['ents_wishlist_collection_removedList'];
    }
    this.closeLayer();
    if (!wasDeletedViaQuickDelete){
      pushToSnackbar(
        `${collectionDeletedText}`.replace('{}', collection.name),
      );
      return;
    }
    pushToSnackbar(
      `${collectionDeletedText}`.replace('{}', collection.name),
      undefined,
      this.messages['ents_wishlist_delete_buttontext'],
      () => this.undoRemove(collection)
    );
  }

  private undoRemove(collectionToUndo: WishlistCollection) {
    collectionApi.add(this.wishlistAppConfig.collectionEndpoint, collectionToUndo.name, collectionToUndo.created)
      .then((result) => {
        if (result?.isOk && result.collection) {
          const collection = result.collection;
          this.collectionsState.add(collection, 'collection_list');
        }
      });
  }

  private backButtonAction(){
    if (this.collectionMenuState.context.backButtonCallback){
      this.collectionMenuState.context.backButtonCallback();
    }
    this.closeLayer();
    this.dispatchEvent(new Event('ents-layer-back', {composed: true, bubbles: true}));
  }

  private closeLayer = () => {
    if (this.collectionMenuState.context.openRemoveCollectionView) {
      this.collectionMenuState.closeRemoveCollectionView();
    }
  };

  private getSafeCollectionNameOrEmpty() {
    const collection = this.collectionMenuState.context.collection!;
    if (collection != null) {
      return collection.name;
    }
    return '';
  }

  private dispatchRemovedCountEvent(count: number) {
    document.dispatchEvent(new CustomEvent(REMOVE_COUNT_FROM_WISHLIST_EVENT, {
      detail: {count},
      composed: true,
      bubbles: true
    }));
  }
}
